/**
 * Time zone helpers, defined once for every consumer: the admin SPA's
 * report date expansion, the POS's local report day boundaries, and the
 * server's per-terminal effective-zone computation.
 *
 * The wall-clock conversion was born in packages/admin/lib/reportDates.ts
 * (see docs/decisions/2026-07-31-report-date-range-timezone.md) and moved
 * here when terminals gained per-device time zones — three packages doing
 * their own zone math is exactly the drift this package exists to prevent.
 */
/** The business's home zone; the default wherever no zone is known. */
export const DEFAULT_TIME_ZONE = 'America/Mexico_City';
/** True if the runtime's Intl data knows this IANA zone name. */
export function isValidTimeZone(timeZone) {
    try {
        new Intl.DateTimeFormat('en-US', { timeZone });
        return true;
    }
    catch {
        return false;
    }
}
/**
 * The device's own IANA zone, as the OS reports it. On Android this follows
 * the automatic time zone setting, which the system derives from the mobile
 * network and location — the practical form of "time zone from GPS".
 */
export function deviceTimeZone() {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone ?? null;
    }
    catch {
        return null;
    }
}
/**
 * Offset, in minutes, of `timeZone` from UTC at the instant `utcMs`.
 * Positive east of UTC, negative west — matches the sign convention of an
 * ISO offset like "-06:00".
 */
function offsetMinutesAt(utcMs, timeZone) {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone,
        hourCycle: 'h23',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    }).formatToParts(new Date(utcMs));
    const get = (type) => parts.find((p) => p.type === type)?.value ?? '0';
    // Some engines render midnight as "24" under hourCycle: 'h23'; normalize.
    const hour = get('hour') === '24' ? '0' : get('hour');
    const asUtc = Date.UTC(Number(get('year')), Number(get('month')) - 1, Number(get('day')), Number(hour), Number(get('minute')), Number(get('second')));
    return (asUtc - utcMs) / 60_000;
}
/**
 * Converts a wall-clock date/time understood to be local to `timeZone` into
 * the UTC instant it names, as an ISO string.
 *
 * One pass (guess, then correct by the offset found at the guess) is exact
 * for any zone that does not change offset within the guessed instant —
 * always true for America/Mexico_City (fixed UTC-6 since Mexico's 2022
 * reform) and true everywhere outside the two DST switch hours a year.
 */
export function zonedWallClockToIso(year, month, day, hour, minute, second, ms, timeZone) {
    // Intl only reports whole seconds; resolve the offset on the
    // whole-second guess and add `ms` back last, or the offset comes back
    // fractional and wrong.
    const guessUtc = Date.UTC(year, month - 1, day, hour, minute, second);
    const offsetMin = offsetMinutesAt(guessUtc, timeZone);
    return new Date(guessUtc - offsetMin * 60_000 + ms).toISOString();
}
/** Calendar date (YYYY-MM-DD) of an instant, in the given zone. */
export function dateInZone(instant, timeZone) {
    return new Intl.DateTimeFormat('en-CA', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).format(instant);
}
function parseDateOnly(value) {
    const [y, mo, d] = value.split('-').map(Number);
    if (y === undefined || mo === undefined || d === undefined || Number.isNaN(y + mo + d)) {
        throw new Error(`not a YYYY-MM-DD date: ${value}`);
    }
    return [y, mo, d];
}
/** [start, end] instants (ISO) of the given calendar day in the zone. */
export function zonedDayBounds(dateISO, timeZone) {
    const [y, mo, d] = parseDateOnly(dateISO);
    return {
        fromISO: zonedWallClockToIso(y, mo, d, 0, 0, 0, 0, timeZone),
        toISO: zonedWallClockToIso(y, mo, d, 23, 59, 59, 999, timeZone),
    };
}
/** The calendar day, in the zone, that lies `days` before `dateISO`. */
export function shiftDateISO(dateISO, days) {
    const [y, mo, d] = parseDateOnly(dateISO);
    const shifted = new Date(Date.UTC(y, mo - 1, d - days));
    return shifted.toISOString().slice(0, 10);
}
/** ISO weekday of a calendar date, Monday = 0 … Sunday = 6. Zone-free: a
 * calendar date has one weekday. */
export function weekdayOfDate(dateISO) {
    const [y, mo, d] = parseDateOnly(dateISO);
    return (new Date(Date.UTC(y, mo - 1, d)).getUTCDay() + 6) % 7;
}
//# sourceMappingURL=timezone.js.map